import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
} from "@mui/material"
import { showWarning } from "../../../../api"

const ModalSeriesIngreso = ({ open, onClose, onSave, articulo, bodcodigo }) => {
  const [rows, setRows] = useState([])
  const isVehiculo = articulo?.tipserie === "V"

  // ==========================================================================
  // INICIALIZACIÓN DINÁMICA DE FILAS
  // ==========================================================================
  useEffect(() => {
    if (open && articulo) {
      const cantidadAAfectar = parseFloat(articulo.cant_afectar) || 0

      // Si ya existían series cargadas y coinciden con la cantidad actual, las restauramos
      if (articulo.series && articulo.series.length === cantidadAAfectar) {
        setRows([...articulo.series])
      } else {
        // Generamos filas vacías basándonos estrictamente en la cantidad a afectar
        const initialRows = Array.from({ length: cantidadAAfectar }, () => ({
          sernumero: "",
          motor: "",
          ram: "",
          anio: "",
          pais: "",
          color: "",
        }))
        setRows(initialRows)
      }
    }
  }, [open, articulo])

  // ==========================================================================
  // MANEJADOR DE CAMBIOS EN LA GRILLA
  // ==========================================================================
  const handleInputChange = (index, field, value) => {
    setRows((prevRows) => {
      const newRows = [...prevRows]
      // Forzamos mayúsculas para mantener consistencia en la base de datos
      newRows[index][field] = field === "anio" ? value : value.toUpperCase()
      return newRows
    })
  }

  // ==========================================================================
  // VALIDACIÓN ESTRICTA Y GUARDADO
  // ==========================================================================
  const handleSave = () => {
    const seriesSet = new Set()

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]

      // 1. Validación de Serie Obligatoria
      if (!row.sernumero.trim()) {
        return showWarning(`La serie en la fila ${i + 1} está en blanco. Debe completarla.`)
      }

      // 2. Validación de Duplicados en Memoria
      if (seriesSet.has(row.sernumero)) {
        return showWarning(`La serie "${row.sernumero}" está duplicada en la fila ${i + 1}. Cada serie debe ser única.`)
      }
      seriesSet.add(row.sernumero)

      // 3. Validación de Campos Extendidos (Si es Vehículo)
      if (isVehiculo) {
        if (!row.motor.trim()) return showWarning(`Falta el Motor en la fila ${i + 1}.`)
        if (!row.ram.trim()) return showWarning(`Falta el RAMV en la fila ${i + 1}.`)
        if (!row.anio || isNaN(row.anio) || parseInt(row.anio) <= 0) {
          return showWarning(`El Año en la fila ${i + 1} no es válido.`)
        }
        if (!row.pais.trim()) return showWarning(`Falta el País en la fila ${i + 1}.`)
        if (!row.color.trim()) return showWarning(`Falta el Color en la fila ${i + 1}.`)
      }
    }

    // Si todo es correcto, enviamos el arreglo al componente padre
    onSave(rows)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth={isVehiculo ? "lg" : "sm"} fullWidth>
      <DialogTitle sx={{ backgroundColor: "#196C87", color: "white", py: 1.5 }}>Ingreso de Nuevas Series</DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Artículo: <b>{articulo?.artcodigo}</b> - {articulo?.artdescri}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Bodega: {bodcodigo} | Cantidad a Ingresar: <b>{articulo?.cant_afectar}</b>
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="5%">#</TableCell>
                <TableCell width={isVehiculo ? "25%" : "95%"}>Número de Serie *</TableCell>
                {isVehiculo && (
                  <>
                    <TableCell width="15%">Motor *</TableCell>
                    <TableCell width="15%">RAMV *</TableCell>
                    <TableCell width="10%">Año *</TableCell>
                    <TableCell width="15%">País *</TableCell>
                    <TableCell width="15%">Color *</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={row.sernumero}
                      onChange={(e) => handleInputChange(index, "sernumero", e.target.value)}
                      inputProps={{ maxLength: 50 }}
                    />
                  </TableCell>
                  {isVehiculo && (
                    <>
                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={row.motor}
                          onChange={(e) => handleInputChange(index, "motor", e.target.value)}
                          inputProps={{ maxLength: 30 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={row.ram}
                          onChange={(e) => handleInputChange(index, "ram", e.target.value)}
                          inputProps={{ maxLength: 30 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          type="number"
                          value={row.anio}
                          onChange={(e) => handleInputChange(index, "anio", e.target.value)}
                          inputProps={{ min: 1900, max: 2100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={row.pais}
                          onChange={(e) => handleInputChange(index, "pais", e.target.value)}
                          inputProps={{ maxLength: 3 }}
                          placeholder="Cod"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={row.color}
                          onChange={(e) => handleInputChange(index, "color", e.target.value)}
                          inputProps={{ maxLength: 3 }}
                          placeholder="Cod"
                        />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: "#f5f7fa" }}>
        <Button onClick={onClose} color="inherit" variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" sx={{ backgroundColor: "#196C87" }}>
          Confirmar Series
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalSeriesIngreso
